/** TanStack Query keys and fetchers — one place per API resource. */

import { api } from '@/services/api';
import type {
  AdminEnrollment,
  AuditLogEntry,
  AuthProviders,
  CandidateResult,
  CatalogStats,
  ChapterDetail,
  ContentReadiness,
  DashboardResponse,
  GroupOut,
  LabDetail,
  LabSolution,
  ModuleDetail,
  ModuleSummary,
  PlatformStats,
  QuizAttemptSummary,
  QuizSummary,
  ResourcePublic,
  TrackDetail,
  TrackProgress,
  TrackSummary,
  UserAdmin,
} from '@/types/api';

export const queryKeys = {
  stats: ['stats'] as const,
  providers: ['auth', 'providers'] as const,
  tracks: ['tracks'] as const,
  track: (slug: string) => ['tracks', slug] as const,
  modules: (filters?: Record<string, string | undefined>) => ['modules', filters ?? {}] as const,
  module: (identifier: string) => ['modules', identifier] as const,
  chapter: (moduleId: string, slug: string) => ['chapter', moduleId, slug] as const,
  labs: (moduleCode: string) => ['labs', moduleCode] as const,
  lab: (id: string) => ['lab', id] as const,
  labSolution: (id: string) => ['lab', id, 'solution'] as const,
  quizzes: (moduleCode: string) => ['quizzes', moduleCode] as const,
  quizAttempts: (quizId: string) => ['quizzes', quizId, 'attempts'] as const,
  dashboard: ['me', 'dashboard'] as const,
  progress: ['me', 'progress'] as const,
  resources: ['me', 'resources'] as const,
  instructorModules: ['instructor', 'modules'] as const,
  instructorGroups: ['instructor', 'groups'] as const,
  instructorModuleContent: (code: string) => ['instructor', 'modules', code, 'content'] as const,
  groupCandidates: (groupId: string) => ['instructor', 'groups', groupId, 'candidates'] as const,
  adminUsers: (filters?: Record<string, string | undefined>) =>
    ['admin', 'users', filters ?? {}] as const,
  adminStats: ['admin', 'stats'] as const,
  adminAudit: ['admin', 'audit'] as const,
  adminReadiness: ['admin', 'content-readiness'] as const,
  adminResults: ['admin', 'results'] as const,
  adminGroups: ['admin', 'groups'] as const,
  adminEnrollments: (status?: string) => ['admin', 'enrollments', status ?? 'all'] as const,
};

export const fetchers = {
  stats: () => api.get<CatalogStats>('/stats'),
  providers: () => api.get<AuthProviders>('/auth/providers'),
  tracks: () => api.get<TrackSummary[]>('/tracks'),
  track: (slug: string) => api.get<TrackDetail>(`/tracks/${slug}`),
  modules: (filters?: { track?: string; search?: string; status?: string }) =>
    api.get<ModuleSummary[]>('/modules', filters),
  module: (identifier: string) => api.get<ModuleDetail>(`/modules/${identifier}`),
  chapter: (moduleIdentifier: string, slug: string) =>
    api.get<ChapterDetail>(`/modules/${moduleIdentifier}/chapters/${slug}`),
  lab: (id: string) => api.get<LabDetail>(`/labs/${id}`),
  labSolution: (id: string) => api.get<LabSolution>(`/labs/${id}/solution`),
  quizzes: (moduleCode: string) => api.get<QuizSummary[]>('/quizzes', { module: moduleCode }),
  quizAttempts: (quizId: string) =>
    api.get<QuizAttemptSummary[]>(`/quizzes/${quizId}/attempts`),
  dashboard: () => api.get<DashboardResponse>('/me/dashboard'),
  progress: () => api.get<TrackProgress[]>('/me/progress'),
  resources: () => api.get<ResourcePublic[]>('/me/resources'),
  instructorModules: () => api.get<ModuleSummary[]>('/instructor/modules'),
  instructorGroups: () => api.get<GroupOut[]>('/instructor/groups'),
  instructorModuleContent: (code: string) =>
    api.get<{
      module: { code: string; title: string; status: string };
      chapters: { slug: string; title: string; is_published: boolean; estimated_minutes: number }[];
      labs: {
        id: string;
        slug: string;
        title: string;
        is_published: boolean;
        has_solution: boolean;
        criteria_count: number;
      }[];
      quizzes: {
        id: string;
        slug: string;
        title: string;
        status: string;
        questions: number;
        pass_score_percent: number;
      }[];
    }>(`/instructor/modules/${code}/content`),
  groupCandidates: (groupId: string) =>
    api.get<CandidateResult[]>(`/instructor/groups/${groupId}/candidates`),
  adminUsers: (filters?: { role?: string; search?: string }) =>
    api.get<UserAdmin[]>('/admin/users', filters),
  adminStats: () => api.get<PlatformStats>('/admin/stats'),
  adminAudit: () => api.get<AuditLogEntry[]>('/admin/audit', { limit: 60 }),
  adminReadiness: () => api.get<ContentReadiness[]>('/admin/content-readiness'),
  adminResults: () => api.get<CandidateResult[]>('/admin/results'),
  adminEnrollments: (status?: string) =>
    api.get<AdminEnrollment[]>('/admin/enrollments', status ? { status } : undefined),
};
